import { Table, TableColumnsType, Button, message, Typography } from 'antd';
import React, { useMemo, useState } from 'react';
import { useLazyQuery, useQuery } from '@apollo/client';
import FilterArea, { FilterOptions, DEFAULT_FILTER_OPTIONS, convertToGraphQLFilters } from './components/FilterArea';
import PermissionPackageDrawer from './components/PermissionPackageDrawer';
import PackageRolesDrawer from './components/PackageRolesDrawer';
import { useTranslation } from 'react-i18next';
import TablePaginationBar from '../../../../components/TablePaginationBar';
import {
  GET_PERMISSION_PACKAGE_LISTING,
  GetPermissionPackageListingResponse,
  PermissionPackageType,
} from './graphql/queries/getPermissionPackageListing';
import {
  GET_PERMISSION_PACKAGE_DETAIL_BY_ID,
  type GetPermissionPackageDetailByIdResponse,
  type PermissionPackageDetailData,
} from './graphql/queries/getPermissionPackageDetailById';
import { HasResource } from '../../../../components/HasResource';
import { ResourceCode } from 'constants/resourceCodes';

const PermissionPackagePage: React.FC = () => {
  const { t } = useTranslation();
  /** 筛选输入草稿：输入/选择时不触发列表查询 */
  const [draftFilterOptions, setDraftFilterOptions] = useState<FilterOptions>(DEFAULT_FILTER_OPTIONS);
  /** 已应用筛选：仅点击“筛选/重置”后触发列表查询 */
  const [appliedFilterOptions, setAppliedFilterOptions] = useState<FilterOptions>(DEFAULT_FILTER_OPTIONS);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerRecord, setDrawerRecord] = useState<PermissionPackageDetailData | null>(null);
  const [loadingDetailId, setLoadingDetailId] = useState<string | null>(null);
  const [rolesDrawerRecord, setRolesDrawerRecord] = useState<PermissionPackageType | null>(null);

  const graphQLFilters = useMemo(() => convertToGraphQLFilters(appliedFilterOptions), [appliedFilterOptions]);
  const variables = useMemo(
    () => ({
      condition: graphQLFilters,
      pageRequest: {
        page: currentPage,
        size: pageSize,
        sortDirection: 'DESC',
      },
    }),
    [graphQLFilters, currentPage, pageSize]
  );

  const { data, loading, refetch } = useQuery<GetPermissionPackageListingResponse>(GET_PERMISSION_PACKAGE_LISTING, {
    variables,
    fetchPolicy: 'cache-and-network',
  });

  const [fetchDetail] = useLazyQuery<GetPermissionPackageDetailByIdResponse>(GET_PERMISSION_PACKAGE_DETAIL_BY_ID, {
    fetchPolicy: 'network-only',
  });

  const handleTableChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const openDrawerById = async (record: PermissionPackageType) => {
    const idNum = Number(record.id);
    setLoadingDetailId(String(record.id));
    try {
      const { data: detailData } = await fetchDetail({ variables: { id: idNum } });
      const d = detailData?.getPermissionPackageDetailById;
      if (!d?.success || !d.data) {
        message.error(String(d?.message || t('central.platformPermission.permissionPackage.messages.detailLoadFail')));
        return;
      }
      setDrawerRecord(d.data);
      setDrawerVisible(true);
    } catch (e: any) {
      message.error(e?.message || String(t('central.platformPermission.permissionPackage.messages.detailLoadFail')));
    } finally {
      setLoadingDetailId(null);
    }
  };

  const columns: TableColumnsType<PermissionPackageType> = [
    {
      title: t('central.platformPermission.permissionPackage.table.id'),
      dataIndex: 'id',
      key: 'id',
      width: 80,
      align: 'left',
      render: (_: unknown, record: PermissionPackageType) => (
        <Button
          type="link"
          size="small"
          className="px-0"
          loading={loadingDetailId === String(record.id)}
          onClick={() => void openDrawerById(record)}
        >
          {record.id}
        </Button>
      ),
    },
    {
      title: t('central.platformPermission.permissionPackage.table.name'),
      dataIndex: 'name',
      key: 'name',
      width: 180,
      align: 'left',
    },
    {
      title: t('central.platformPermission.permissionPackage.table.description'),
      dataIndex: 'description',
      key: 'description',
      ellipsis: { showTitle: false },
      align: 'left',
      width: 220,
      render: (text: string | null) => (
        <Typography.Text style={{ display: 'block' }} ellipsis={{ tooltip: text ?? '-' }}>
          {text ?? '-'}
        </Typography.Text>
      ),
    },
    {
      title: t('components.button.action'),
      key: 'linkedRoles',
      width: 120,
      align: 'left',
      render: (_: unknown, record: PermissionPackageType) => (
        <HasResource resourceCodes={[ResourceCode.menu.PERMISSION_PACKAGE_QUERY]}>
          <Button
            type="link"
            size="small"
            className="px-0"
            onClick={() => setRolesDrawerRecord(record)}
          >
            {t('central.platformPermission.permissionPackage.table.linkedRoles')}
          </Button>
        </HasResource>
      ),
    },
  ];

  const pageData = data?.getPermissionPackageListing;

  return (
    <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
      <FilterArea filters={draftFilterOptions} setFilters={setDraftFilterOptions} />

      <div className="flex justify-between items-center gap-2 mb-3 pl-0">
        <HasResource resourceCodes={[ResourceCode.permissionPackage.ADD]}>
          <Button
              type="primary"
              className="w-auto min-w-24"
              onClick={() => {
                setDrawerRecord(null);
                setDrawerVisible(true);
              }}
          >
            <span className="font-bold">{t('central.platformPermission.permissionPackage.addButton')}</span>
          </Button>
        </HasResource>
        <div className="flex items-center gap-2">
          <Button
            type="primary"
            onClick={() => {
              setAppliedFilterOptions({ ...draftFilterOptions });
              setCurrentPage(1);
            }}
            className="w-auto min-w-24"
          >
            <span className="font-bold">{t('components.button.filter')}</span>
          </Button>
          <Button
            onClick={() => {
              setDraftFilterOptions(DEFAULT_FILTER_OPTIONS);
              setAppliedFilterOptions(DEFAULT_FILTER_OPTIONS);
              setCurrentPage(1);
            }}
            className="w-auto min-w-24"
          >
            <span className="font-bold">{t('components.button.reset')}</span>
          </Button>
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={pageData?.content ?? []}
        loading={loading}
        rowKey="id"
        pagination={false}
        size="middle"
        tableLayout="fixed"
        className="mb-4"
      />

      <TablePaginationBar
        total={pageData?.totalElements ?? 0}
        current={currentPage}
        pageSize={pageSize}
        onChange={(page, size) => handleTableChange(page, size)}
        onShowSizeChange={(_, size) => {
          setCurrentPage(1);
          setPageSize(size);
        }}
        showQuickJumper={false}
      />

      <PermissionPackageDrawer
        visible={drawerVisible}
        onClose={() => {
          setDrawerVisible(false);
          setDrawerRecord(null);
        }}
        editRecord={drawerRecord}
        onSuccess={() => void refetch()}
      />

      <PackageRolesDrawer
        visible={rolesDrawerRecord != null}
        packageId={rolesDrawerRecord?.id ?? ''}
        packageName={rolesDrawerRecord?.name ?? ''}
        onClose={() => setRolesDrawerRecord(null)}
      />
    </div>
  );
};

export default PermissionPackagePage;
